import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import styles from './input-field-text.module.scss';

interface InputFieldTextProps {
  className?: string;
  text: string;
  icon?: string;
  type: InputFieldTextType;
  size?: InputFieldSize;
  isBold?: boolean;
  isDisabled?: boolean;
}

export enum InputFieldTextType {
  LABEL_TEXT = 'LABEL_TEXT',
  /** The "length/max" counter beside the label: label-sized, muted, no icon. */
  CHARACTER_COUNT_TEXT = 'CHARACTER_COUNT_TEXT',
  DEFAULT_INFORMATION_TEXT = 'DEFAULT_INFORMATION_TEXT',
  ERROR_INFORMATION_TEXT = 'ERROR_INFORMATION_TEXT',
  SUCCESS_INFORMATION_TEXT = 'SUCCESS_INFORMATION_TEXT',
}

export function InputFieldText({
  type = InputFieldTextType.LABEL_TEXT,
  icon = '',
  size = InputFieldSize.SMALL,
  className = '',
  isBold = false,
  isDisabled = false,
  ...props
}: InputFieldTextProps): ReactElement {
  const getColorForContainer = (): string => {
    switch (type) {
      case InputFieldTextType.LABEL_TEXT:
        if (isDisabled) {
          return styles.supplementaryContainerColor;
        }
        return styles.standardContainerColor;
      case InputFieldTextType.CHARACTER_COUNT_TEXT:
      case InputFieldTextType.DEFAULT_INFORMATION_TEXT:
        return styles.supplementaryContainerColor;
      case InputFieldTextType.ERROR_INFORMATION_TEXT:
        return 'content-danger';
      case InputFieldTextType.SUCCESS_INFORMATION_TEXT:
        return 'content-success';
      default:
        return '';
    }
  };

  const getIcon = (): string => {
    if (icon) {
      return icon;
    }
    switch (type) {
      case InputFieldTextType.DEFAULT_INFORMATION_TEXT:
        return 'icon-info';
      case InputFieldTextType.ERROR_INFORMATION_TEXT:
        return 'icon-exclamation-triangle';
      case InputFieldTextType.SUCCESS_INFORMATION_TEXT:
        return 'icon-check-circle';
      default:
        return '';
    }
  };

  const isLabelRowText = type === InputFieldTextType.LABEL_TEXT || type === InputFieldTextType.CHARACTER_COUNT_TEXT;

  const getTextSize = (): TextSize => {
    if (!isLabelRowText) {
      return TextSize.XXSMALL;
    }
    switch (size) {
      case InputFieldSize.XSMALL:
      case InputFieldSize.SMALL:
        return TextSize.XXSMALL;
      case InputFieldSize.MEDIUM:
        return TextSize.XSMALL;
      default:
        return TextSize.XXSMALL;
    }
  };

  const getContainerClasses = (): string => {
    const classes = ['flex-row-align-center', getColorForContainer(), className];

    return classes.join(' ');
  };

  return (
    <div className={getContainerClasses()}>
      {!isLabelRowText ? <Icon icon={getIcon()} size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" /> : null}
      <Text val={props.text} className="" size={getTextSize()} bold={isBold} />
    </div>
  );
}
